// NPM
import { computed } from 'vue';

// API
import { useChallenges } from '@/api/challenges/use-challenges';
import { useEvents } from '@/api/events/use-events';
import { useRelations } from '@/api/relations/use-relations';
import { useUgcs } from '@/api/ugcs/use-ugcs';

export const isEventReadyForAmbassadorAction = (event: {
  registrationStatus: string | null;
  participationConfirmedAt: string | Date | null;
  participationStatus: string | null;
  date: string | Date;
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventDate = new Date(event.date);
  eventDate.setHours(0, 0, 0, 0);

  return (
    event.registrationStatus === 'validated' &&
    event.participationStatus === null &&
    eventDate <= today &&
    !event.participationConfirmedAt
  );
};

export function useHasPendingAction(disabled?: boolean) {
  const { data: challenges } = useChallenges(disabled);
  const { data: events } = useEvents(disabled);
  const { data: ugcs } = useUgcs(disabled);
  const { data: relations } = useRelations(disabled);

  const hasChallengeAction = computed(() => {
    return (
      challenges.value?.ongoing.some(
        (challenge) => challenge.status === 'interested'
      ) ?? false
    );
  });

  const hasEventAction = computed(() => {
    return events.value?.ongoing.some(isEventReadyForAmbassadorAction) ?? false;
  });

  const hasUgcAction = computed(() => {
    return (
      ugcs.value?.ongoing.some(
        (ugc) =>
          ugc.status === 'interested' || ugc.status === 'modificationRequested'
      ) ?? false
    );
  });

  /**
   * Only the states where there is something the ambassador can do right now, with no ambiguity:
   * write the first message, or confirm an exchange the prospect has already confirmed.
   * 'contacted' is left out — the conversation may simply still be running.
   */
  const hasRelationAction = computed(() =>
    (relations.value?.toProcess ?? []).some((relation) =>
      ['toContact', 'toConfirm'].includes(relation.state)
    )
  );

  return {
    hasChallengeAction,
    hasEventAction,
    hasUgcAction,
    hasRelationAction,
  };
}
